/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module LogsModule
 */

import {
    Component,
    Input,
    TemplateRef,
    ViewChild,
} from '@angular/core';

import { L10nService } from '@vmw/ngx-vip';
import { IAviDataGridConfig } from 'ng/modules/data-grid';

import { TrafficLogCinematicStore } from '../../traffic-logs/traffic-log-cinematic/traffic-log-cinematic.store';
import { L4ConnectionInfoType } from '../../traffic-logs/traffic-log-cinematic/traffic-log-cinematic.types';
import { L4ConnectionType } from '../../traffic-logs/traffic-log-cinematic/traffic-log-cinematic.component';
import * as l10n from './log-cinematic-section-connection.l10n';

import './log-cinematic-section-connection.component.less';

const { ENGLISH: dictionary, ...l10nKeys } = l10n;

/**
 * @description
 *     Component for client/server connection logs in traffic logs c-view.
 * @author Kondiparthi Shanmukha Sarath, Zhiqian Liu
 */
@Component({
    selector: 'log-cinematic-section-connection',
    templateUrl: 'log-cinematic-section-connection.component.html',
})
export class LogCinematicSectionConnection {
    /**
     * Host type for connection logs.
     */
    @Input()
    public l4ConnectionType: L4ConnectionType;

    /**
     * TeamplteRef for packets, transfers and connection issues grid rows.
     */
    @ViewChild('rowFieldTemplateRef')
    private readonly rowFieldTemplateRef: TemplateRef<HTMLElement>;

    /**
     * Hashmap for host type and connections logs type.
     */
    public readonly hostTypeConnectionLog = {
        [L4ConnectionType.SERVER]: this.logCinematicStore.l4ServerConnectionInfo$,
        [L4ConnectionType.CLIENT]: this.logCinematicStore.l4ClientConnectionInfo$,
    };

    /**
     * Log types for a specific host (client or server).
     */
    public readonly l4ConnectionInfoType = {
        [L4ConnectionInfoType.CONNECTION_ISSUES]: L4ConnectionInfoType.CONNECTION_ISSUES,
        [L4ConnectionInfoType.PACKETS]: L4ConnectionInfoType.PACKETS,
        [L4ConnectionInfoType.TRANSFERS]: L4ConnectionInfoType.TRANSFERS,
    };

    public packetsGridConfig: IAviDataGridConfig;

    public transfersGridConfig: IAviDataGridConfig;

    public connectionIssuesGridConfig: IAviDataGridConfig;

    constructor(
        private readonly l10nService: L10nService,
        public readonly logCinematicStore: TrafficLogCinematicStore,
    ) {
        l10nService.registerSourceBundles(dictionary);
    }

    public ngAfterViewInit(): void {
        this.packetsGridConfig = this.getGridConfig(
            this.l4ConnectionInfoType.PACKETS,
            l10nKeys.packetsLabel,
        );

        this.transfersGridConfig = this.getGridConfig(
            this.l4ConnectionInfoType.TRANSFERS,
            l10nKeys.transfersLabel,
        );

        this.connectionIssuesGridConfig = this.getGridConfig(
            this.l4ConnectionInfoType.CONNECTION_ISSUES,
            l10nKeys.connectionIssuesLabel,
        );
    }

    /**
     * Set and return packets, transfers and connection issues grid configs.
     */
    private getGridConfig(id: string, label: string): IAviDataGridConfig {
        const gridConfig: IAviDataGridConfig = {
            layout: {
                hidePagination: true,
                hideCheckboxes: true,
                showFooter: false,
            },
            fields: [
                {
                    label: this.l10nService.getMessage(label),
                    id,
                    templateRef: this.rowFieldTemplateRef,
                },
            ],
        };

        return gridConfig;
    }
}
